/**
 * Frontend-only utility to export the currently open AI Tutor conversation
 * as a PDF document.
 *
 * FIXES (while KEEPING "• + Capital" / "• - Drawings"):
 * - Prevents accidental list creation from hyphens inside normal paragraphs
 * - Hardens inline "prefix: - item - item" detection (requires 2+ items)
 * - Moves dash normalization to render-time only (safe)
 * - Cleans control chars / zero-width spaces reliably
 */

import { jsPDF } from "jspdf"
import type { ChatMessage } from "@/models"

const FONT_URL = "/fonts/NotoSans-Regular.ttf"
const FONT_FILE = "NotoSans-Regular.ttf"
const FONT_FAMILY = "NotoSans"

const SYMBOL_FONT_URL = "/fonts/NotoSansSymbols-Regular.ttf"
const SYMBOL_FONT_FILE = "NotoSansSymbols-Regular.ttf"
const SYMBOL_FONT_FAMILY = "NotoSansSymbols"

const arrayBufferToBase64 = (buffer: ArrayBuffer) => {
  let binary = ""
  const bytes = new Uint8Array(buffer)
  const chunkSize = 0x8000
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize))
  }
  return btoa(binary)
}

const registerUnicodeFont = async (doc: jsPDF) => {
  let hasBase = false
  let hasSymbol = false

  try {
    const res = await fetch(FONT_URL)
    if (res.ok) {
      const buffer = await res.arrayBuffer()
      const base64 = arrayBufferToBase64(buffer)

      doc.addFileToVFS(FONT_FILE, base64)
      doc.addFont(FONT_FILE, FONT_FAMILY, "normal")
      doc.addFont(FONT_FILE, FONT_FAMILY, "bold")
      doc.addFont(FONT_FILE, FONT_FAMILY, "italic")
      doc.addFont(FONT_FILE, FONT_FAMILY, "bolditalic")
      hasBase = true
    }
  } catch {
    hasBase = false
  }

  try {
    const res = await fetch(SYMBOL_FONT_URL)
    if (res.ok) {
      const buffer = await res.arrayBuffer()
      const base64 = arrayBufferToBase64(buffer)

      doc.addFileToVFS(SYMBOL_FONT_FILE, base64)
      doc.addFont(SYMBOL_FONT_FILE, SYMBOL_FONT_FAMILY, "normal")
      hasSymbol = true
    }
  } catch {
    hasSymbol = false
  }

  return { hasBase, hasSymbol }
}

export async function exportConversationToPdf(params: {
  messages: ChatMessage[]
  courseDisplayName?: string | null
  title?: string
}) {
  const { messages, courseDisplayName, title } = params

  const exportable = messages.filter(
    (m) => m.role === "user" || m.role === "assistant" || m.role === "error",
  )

  const doc = new jsPDF({ unit: "pt", format: "letter" })
  const fontStatus = await registerUnicodeFont(doc)
  const baseFont = fontStatus.hasBase ? FONT_FAMILY : "helvetica"
  const hasSymbolFont = fontStatus.hasSymbol
  const setBaseFont = (style: "normal" | "bold" | "italic" | "bolditalic" = "normal") => {
    doc.setFont(baseFont, style)
  }
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()

  const marginX = 40
  const marginTop = 50
  const bottomMargin = 40

  const lineGap = 16
  const maxTextWidth = pageWidth - marginX * 2
  const codeFontSize = 10

  let y = marginTop
  const now = new Date()

  const ensureSpace = (needed: number) => {
    if (y + needed > pageHeight - bottomMargin) {
      doc.addPage()
      y = marginTop
      return true
    }
    return false
  }

  // ----------------------------
  // Header
  // ----------------------------
  setBaseFont("bold")
  doc.setFontSize(20)
  doc.text(title ?? "AI Tutor Conversation", marginX, y)
  y += 28

  setBaseFont("normal")
  doc.setFontSize(11)
  doc.text(`Course: ${courseDisplayName ?? "N/A"}`, marginX, y)
  y += 16
  doc.text(`Exported: ${now.toLocaleString()}`, marginX, y)
  y += 20

  // ----------------------------
  // Parsing helpers
  // ----------------------------

  // collapse sequences like "l e g a l" into "legal" (heuristic)
  const collapseSpacedLetters = (s: string) => {
    return s.replace(/(?:\b|^)(?:[A-Za-z]\s+){2,}[A-Za-z](?:\b|$)/g, (m) => m.replace(/\s+/g, ""))
  }

  /**
   * Convert LaTeX math notation to readable text with Unicode symbols
   * Handles nested expressions like fractions and square roots
   */
  const convertLatexToUnicode = (text: string): string => {
    // Handle display math $$...$$ and inline math $...$
    return text.replace(/\$\$([\s\S]+?)\$\$|\$(.+?)\$/g, (match, display, inline) => {
      let latex = display || inline

      // Unicode maps for superscript/subscript (defined once, used in loop)
      const SUP_MAP: Record<string, string> = {
        '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴', '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹',
        '+': '⁺', '-': '⁻', '=': '⁼'
      }
      const toSup = (s: string) => s.split('').map((ch) => SUP_MAP[ch] ?? ch).join('')
      const SUB_MAP: Record<string, string> = {
        '0': '₀', '1': '₁', '2': '₂', '3': '₃', '4': '₄', '5': '₅', '6': '₆', '7': '₇', '8': '₈', '9': '₉'
      }
      const toSub = (s: string) => s.split('').map((ch) => SUB_MAP[ch] ?? ch).join('')

      // Process nested structures multiple times (inside-out)
      // Superscripts/subscripts MUST be inside the loop so that e.g. ^{3} resolves
      // to ³ before the next fraction pass — otherwise \frac{π d^{3}}{6} fails
      // because [^{}]+ can't match content with nested braces.
      for (let i = 0; i < 3; i++) {
        // Greek letters
        latex = latex.replace(/\\alpha\b/g, 'α').replace(/\\beta\b/g, 'β')
          .replace(/\\gamma\b/g, 'γ').replace(/\\delta\b/g, 'δ')
          .replace(/\\epsilon\b/g, 'ε').replace(/\\theta\b/g, 'θ')
          .replace(/\\lambda\b/g, 'λ').replace(/\\mu\b/g, 'μ')
          .replace(/\\pi\b/g, 'π').replace(/\\sigma\b/g, 'σ')
          .replace(/\\tau\b/g, 'τ').replace(/\\phi\b/g, 'φ')
          .replace(/\\omega\b/g, 'ω').replace(/\\Delta\b/g, 'Δ')
          .replace(/\\Sigma\b/g, 'Σ').replace(/\\Omega\b/g, 'Ω')
        
        // Mathematical operators
        latex = latex.replace(/\\times\b/g, '×').replace(/\\div\b/g, '÷')
          .replace(/\\pm\b/g, '±').replace(/\\mp\b/g, '∓')
          .replace(/\\leq\b/g, '≤').replace(/\\geq\b/g, '≥')
          .replace(/\\neq\b/g, '≠').replace(/\\approx\b/g, '≈')
          .replace(/\\equiv\b/g, '≡').replace(/\\infty\b/g, '∞')
          .replace(/\\sum\b/g, '∑').replace(/\\prod\b/g, '∏')
          .replace(/\\int\b/g, '∫').replace(/\\partial\b/g, '∂')
          .replace(/\\nabla\b/g, '∇').replace(/\\cdot\b/g, '·')
          .replace(/\\ldots\b/g, '…').replace(/\\dots\b/g, '…')
        
        // Nested square roots (innermost first)
        latex = latex.replace(/\\sqrt\{([^{}]+)\}/g, '√($1)')
        latex = latex.replace(/\\sqrt\[(\d+)\]\{([^{}]+)\}/g, '$1√($2)')

        // Superscripts (inside loop so ^{3} resolves before next fraction pass)
        latex = latex.replace(/\^{([^}]+)}/g, (_m: string, g: string) => toSup(g))
        latex = latex.replace(/\^(\d+)/g, (_m: string, g: string) => toSup(g))

        // Subscripts (inside loop for same reason)
        latex = latex.replace(/_{([^}]+)}/g, (_m: string, g: string) => toSub(g))
        latex = latex.replace(/_(\d+)/g, (_m: string, g: string) => toSub(g))

        // Nested fractions (innermost first) - handle carefully
        // Support \frac{a}{b}, \dfrac{a}{b}, and common unbraced forms like "\frac a b".
        latex = latex.replace(/\\d?frac\{([^{}]+)\}\{([^{}]+)\}/g, '($1)/($2)')
        latex = latex.replace(/\\d?frac\s*\(?\s*([^\s(){}]+(?:\s[^\s(){}]+)*)\s*\)?\s*\(?\s*([^\s(){}]+(?:\s[^\s(){}]+)*)\s*\)?/g, '($1)/($2)')
        // Catch stray 'frac' or 'dfrac' without leading backslash (robust cleanup)
        latex = latex.replace(/\b(d?frac)\s*\(?\s*([^\s(){}]+(?:\s+[^\s(){}]+)*)\s*\)?\s*\(?\s*([^\s(){}]+(?:\s+[^\s(){}]+)*)\s*\)?/g, '($2)/($3)')

        // Final cleanup: any remaining 'frac' tokens (possibly missing backslashes
        // or braces) — try a loose match: 'frac <numerator> <denominator>' -> '(num)/(den)'
        latex = latex.replace(/\b(?:\\)?d?frac\b\s*([^\s{}()]+(?:\s+[^\s{}()]+)*)\s+([^\s{}()]+(?:\s+[^\s{}()]+)*)/g, '($1)/($2)')
      }
      
      // Remove TeX spacing commands and sizing tokens like \left / \right
      latex = latex.replace(/\\(?:left|right)\b/g, '')
      latex = latex.replace(/\\(?:quad|qquad|,|;|:|!)/g, ' ')

      // Remove remaining backslash-letter commands (e.g., \pi already handled above)
      latex = latex.replace(/\\([a-zA-Z]+)\b/g, '$1')
      latex = latex.replace(/\{([^}]*)\}/g, '$1')
      latex = latex.replace(/\s+/g, ' ')
      
      return latex.trim()
    })
  }

  /**
   * Clean up stray LaTeX tokens that appear OUTSIDE of $...$ delimiters.
   * This catches cases where the AI outputs bare \frac, \pi, etc. without
   * wrapping them in math delimiters.
   */
  const cleanupStrayLatex = (text: string): string => {
    let s = text

    // Unicode maps for superscript/subscript
    const SUP_MAP: Record<string, string> = {
      '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴', '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹',
      '+': '⁺', '-': '⁻', '=': '⁼'
    }
    const toSup = (str: string) => str.split('').map((ch) => SUP_MAP[ch] ?? ch).join('')
    const SUB_MAP: Record<string, string> = {
      '0': '₀', '1': '₁', '2': '₂', '3': '₃', '4': '₄', '5': '₅', '6': '₆', '7': '₇', '8': '₈', '9': '₉'
    }
    const toSub = (str: string) => str.split('').map((ch) => SUB_MAP[ch] ?? ch).join('')

    // Process in a loop: Greek + super/subscripts must resolve BEFORE fractions
    // so that \frac{\pi d^{3}}{6} becomes \frac{π d³}{6} → (π d³)/(6)
    for (let pass = 0; pass < 3; pass++) {
      // Greek letters and math commands
      s = s.replace(/\\pi\b/g, 'π').replace(/\\alpha\b/g, 'α').replace(/\\beta\b/g, 'β')
        .replace(/\\gamma\b/g, 'γ').replace(/\\delta\b/g, 'δ').replace(/\\theta\b/g, 'θ')
        .replace(/\\sigma\b/g, 'σ').replace(/\\mu\b/g, 'μ').replace(/\\lambda\b/g, 'λ')
        .replace(/\\omega\b/g, 'ω').replace(/\\phi\b/g, 'φ').replace(/\\epsilon\b/g, 'ε')
        .replace(/\\tau\b/g, 'τ').replace(/\\Delta\b/g, 'Δ').replace(/\\Sigma\b/g, 'Σ')
        .replace(/\\Omega\b/g, 'Ω').replace(/\\infty\b/g, '∞')
        .replace(/\\times\b/g, '×').replace(/\\div\b/g, '÷')
        .replace(/\\pm\b/g, '±').replace(/\\approx\b/g, '≈')
        .replace(/\\neq\b/g, '≠').replace(/\\leq\b/g, '≤').replace(/\\geq\b/g, '≥')
        .replace(/\\cdot\b/g, '·').replace(/\\sqrt\{([^{}]+)\}/g, '√($1)')

      // Superscripts (before fractions so ^{3} inside frac args resolves first)
      s = s.replace(/\^{([^}]+)}/g, (_m: string, g: string) => toSup(g))
      s = s.replace(/\^(\d+)/g, (_m: string, g: string) => toSup(g))

      // Subscripts
      s = s.replace(/_{([^}]+)}/g, (_m: string, g: string) => toSub(g))
      s = s.replace(/_(\d+)/g, (_m: string, g: string) => toSub(g))

      // Fractions with braces (now inner braces from ^{} are gone)
      s = s.replace(/\\?d?frac\s*\{([^{}]+)\}\s*\{([^{}]+)\}/g, '($1)/($2)')
    }

    // Handle bare "frac" or "dfrac" followed by two tokens (e.g., "fracπ d³6")
    s = s.replace(/\b(?:\\)?d?frac\s*([^\s{}()]+)\s+([^\s{}()]+)/g, '($1)/($2)')

    // Handle \left, \right, spacing commands
    s = s.replace(/\\(?:left|right)\b/g, '')
    s = s.replace(/\\(?:quad|qquad|,|;|:|!)/g, ' ')

    // Remove remaining \command tokens
    s = s.replace(/\\([a-zA-Z]+)\b/g, '$1')

    // Remove leftover braces
    s = s.replace(/\{([^}]*)\}/g, '$1')

    return s
  }

  /**
   * IMPORTANT: Do NOT normalize unicode dashes here.
   * That can turn normal text into patterns that accidentally look like list/table separators.
   * We only normalize dashes at render-time (purely visual).
   */
  const sanitizeLine = (l: string) => {
    const step1 = String(l)
      .replace(/\[doc\d+\]/g, "")
      .replace(/\[\s*\d+(?:\s*,\s*\d+)+\s*\]/g, "")
      .replace(/\(\s*\d+(?:\s*,\s*\d+)+\s*\)/g, "")
      .replace(/([.!?])\s*\d+(?:\s*,\s*\d+)+(?=\s|$)/g, "$1")
      .replace(/\s+\d+(?:\s*,\s*\d+)+(?=\s|$)/g, "")

    // Convert LaTeX math notation to Unicode (handles $...$ and $$...$$ blocks)
    const step1a = convertLatexToUnicode(step1)

    // Clean up any stray LaTeX tokens that appeared outside of $ delimiters
    const step1b = cleanupStrayLatex(step1a)

    const step2 = step1b.replace(/[\u0000-\u001F\u007F-\u009F\u200B\uFEFF]/g, "")
    const step3 = step2.replace(/\s+/g, " ").trim()
    const step4 = collapseSpacedLetters(step3)
    const step5 = step4
      .replace(/\s+([,.;:!?])/g, "$1")
      .replace(/,\s*\./g, ".")
      .replace(/\.\s*,/g, ".")
      .replace(/-\s*\./g, ".")
      .replace(/\s*,\s*$/g, "")
    return step5
  }

  type Segment =
    | { type: "heading"; content: string; level: number }
    | { type: "para"; content: string }
    | { type: "list"; content: string[] }
    | { type: "quote"; content: string[] }
    | { type: "code"; content: string[] }
    | { type: "table"; content: string[][] }

  function parseSegments(raw: string): Segment[] {
    const segments: Segment[] = []

    // Merge multi-line display math blocks ($$...$$) into a single line so
    // `convertLatexToUnicode` (which operates on single-line strings via
    // `sanitizeLine`) can detect and convert them correctly.
    const merged = String(raw).replace(/\$\$([\s\S]*?)\$\$/g, (m) => m.replace(/\r?\n/g, " "))
    const lines = merged.split(/\r?\n/).map(sanitizeLine)

    let i = 0
    while (i < lines.length) {
      const line = lines[i]

      // skip empty lines
      if (!line) {
        i++
        continue
      }

      // skip horizontal rules like --- (markdown)
      if (/^\s*-{3,}\s*$/.test(line)) {
        i++
        continue
      }

      // heading: ## Heading
      const headingMatch = line.match(/^(#{1,6})\s*(.*)$/)
      if (headingMatch) {
        segments.push({
          type: "heading",
          content: headingMatch[2].trim(),
          level: headingMatch[1].length,
        })
        i++
        continue
      }

      // fenced code block ```
      if (line.trim().startsWith("```")) {
        const fence = line.trim()
        const codeLines: string[] = []
        i++
        while (i < lines.length && !lines[i].trim().startsWith("```")) {
          codeLines.push(lines[i])
          i++
        }
        if (i < lines.length && lines[i].trim().startsWith("```")) i++
        
        segments.push({ type: "code", content: codeLines })
        continue
      }

      // ASCII table: header line followed by separator line like "-----  -----"
      if (i + 1 < lines.length && /^\s*(-+\s+)+-+\s*$/.test(lines[i + 1])) {
        const headerLine = lines[i].trim()
        const rows: string[][] = []
        const splitRow = (r: string) => r.trim().split(/\s{2,}/).map((c) => c.trim())
        const headerCells = splitRow(headerLine)
        let j = i + 2
        while (j < lines.length && lines[j].trim() !== "") {
          rows.push(splitRow(lines[j]))
          j++
        }
        segments.push({ type: "table", content: [headerCells, ...rows] })
        i = j
        continue
      }

      // Pipe-delimited table
      if (line.includes("|")) {
        const tableRows: string[] = []
        let j = i
        while (j < lines.length && lines[j].includes("|")) {
          tableRows.push(lines[j])
          j++
        }

        const rows = tableRows
          .map((r) => r.split("|").map((c) => c.trim()).filter(Boolean))
          .filter((r) => r.length > 0 && !r.every((c) => /^:?-+:?$/.test(c)))

        if (rows.length > 0) {
          segments.push({ type: "table", content: rows })
          i = j
          continue
        }
      }

      // List block: -, *, +, •, or numbered
      if (/^\s*[-*+•]\s+/.test(line) || /^\s*\d+\.\s+/.test(line)) {
        const items: string[] = []
        while (
          i < lines.length &&
          ( /^\s*[-*+•]\s+/.test(lines[i]) || /^\s*\d+\.\s+/.test(lines[i]) )
        ) {
          items.push(lines[i].replace(/^\s*[-*+•]\s+|^\s*\d+\.\s+/, "").trim())
          i++
        }

        // keep your +/- if user typed them inside the item content
        const cleaned = items
          .map((it) => it.replace(/\*+/g, "").trim())
          .filter(Boolean)

        if (cleaned.length > 0) segments.push({ type: "list", content: cleaned })
        continue
      }

      // Blockquote >
      if (/^\s*>\s+/.test(line)) {
        const q: string[] = []
        while (i < lines.length && /^\s*>\s+/.test(lines[i])) {
          q.push(lines[i].replace(/^\s*>\s+/, "").trim())
          i++
        }
        segments.push({ type: "quote", content: q })
        continue
      }

      // Paragraph: accumulate until blank or a hard separator (but don't swallow list/quote/heading/code lines)
      const paraLines: string[] = []
      while (
        i < lines.length &&
        lines[i].trim() !== "" &&
        !/^\s*-{3,}\s*$/.test(lines[i]) &&
        !/^\s*[-*+•]\s+/.test(lines[i]) &&
        !/^\s*\d+\.\s+/.test(lines[i]) &&
        !/^\s*>\s+/.test(lines[i]) &&
        !/^(#{1,6})\s+/.test(lines[i]) &&
        !lines[i].trim().startsWith("```")
      ) {
        paraLines.push(lines[i])
        i++
      }
      while (i < lines.length && lines[i].trim() === "") i++

      if (paraLines.length > 0) {
        const paragraph = paraLines.join(" ").replace(/\*+/g, "").trim()

        /**
         * Harden inline "prefix: - item - item" parsing:
         * Only triggers if there are TWO OR MORE real inline items.
         * Prevents equations like "A = B + C - D + E - F" from being mis-parsed.
         */
        const inlineListMatch = paragraph.match(/^(.*?):\s*((?:[-+•]\s+[^-+•]+){2,})$/)
        if (inlineListMatch) {
          const prefix = inlineListMatch[1].trim()
          const rest = inlineListMatch[2].trim()

          // Split items by finding occurrences of "- item" / "+ item" / "• item"
          const found = rest.match(/[-+•]\s+[^-+•]+/g) || []
          const items = found.map((s) => s.trim()) // keep sign in text (you want • + / • -)

          segments.push({ type: "para", content: prefix })
          segments.push({ type: "list", content: items })
        } else {
          const hasInlineDashList =
            paragraph.includes(" - ") &&
            (paragraph.match(/\s-\s/g) || []).length >= 2 &&
            !/[=+]/.test(paragraph)

          if (hasInlineDashList) {
            const parts = paragraph.split(/\s-\s/).map((p) => p.trim()).filter(Boolean)
            if (parts.length >= 3) {
              const [prefix, ...items] = parts
              segments.push({ type: "para", content: prefix })
              segments.push({ type: "list", content: items })
            } else {
              segments.push({ type: "para", content: paragraph })
            }
          } else {
            segments.push({ type: "para", content: paragraph })
          }
        }
      }
    }

    return segments
  }

  // ----------------------------
  // Render helpers
  // ----------------------------

  /**
   * Normalize characters that jsPDF can struggle with.
   * IMPORTANT: Do this at render-time (visual only), not parse-time (semantic).
   */
  const normalizeForPdf = (s: string) => {
    const normalized = String(s)
      .replace(/[\u2018\u2019]/g, "'")
      .replace(/[\u201C\u201D]/g, '"')
      .replace(/\u2264/g, "<=")
      .replace(/\u2265/g, ">=")
      .replace(/\u00D7/g, "x")
      .replace(/\u00F7/g, "/")
      .replace(/[\u2010\u2011\u2012\u2013\u2014\u2015\u00AD\u058A\u1806\u2212\uFE63\uFF0D]/g, "-")
      .replace(/[\u0000-\u001F\u007F-\u009F]/g, "")
      .replace(/\s+/g, " ")
      .trim()

    const withArrows = hasSymbolFont
      ? normalized
      : normalized
          .replace(/\u2192/g, "->")
          .replace(/\u2190/g, "<-")
          .replace(/\u2194/g, "<->")
          .replace(/\u21D2/g, "=>")
          .replace(/\u21D0/g, "<=")
          .replace(/\u21D4/g, "<=>")

    return collapseSpacedLetters(withArrows)
  }

  const SYMBOL_REGEX = /[\u2190-\u21FF\u27F0-\u27FF\u2900-\u297F\u2B00-\u2BFF]/

  const splitBySymbols = (text: string) => {
    const parts: Array<{ text: string; isSymbol: boolean }> = []
    if (!text) return parts

    let current = ""
    let currentIsSymbol = SYMBOL_REGEX.test(text[0] ?? "")

    for (const ch of text) {
      const isSymbol = SYMBOL_REGEX.test(ch)
      if (isSymbol === currentIsSymbol) {
        current += ch
      } else {
        if (current) parts.push({ text: current, isSymbol: currentIsSymbol })
        current = ch
        currentIsSymbol = isSymbol
      }
    }

    if (current) parts.push({ text: current, isSymbol: currentIsSymbol })
    return parts
  }

  const renderTextWithSymbols = (
    text: string,
    style: "normal" | "bold" | "italic" | "bolditalic",
    fontSize: number,
    x: number,
    yPos: number,
  ) => {
    let cursorX = x
    const parts = splitBySymbols(text)
    for (const part of parts) {
      if (part.isSymbol && hasSymbolFont) {
        doc.setFont(SYMBOL_FONT_FAMILY, "normal")
      } else {
        setBaseFont(style)
      }
      doc.setFontSize(fontSize)
      doc.text(part.text, cursorX, yPos)
      cursorX += doc.getTextWidth(part.text)
    }
    return cursorX
  }

  function renderInlineLine(
    line: string,
    x: number,
    yPos: number,
    options: { fontSize?: number; baseStyle?: "normal" | "bold" | "italic" | "bolditalic" } = {},
  ) {
    line = normalizeForPdf(line)

    const fontSize = options.fontSize ?? 11
    const baseStyle = options.baseStyle ?? "normal"

    // simple inline markdown: **bold**, *italic*
    const inlineRegex = /(\*\*([^*]+)\*\*)|(\*([^*]+)\*)/g
    let lastIndex = 0
    let match: RegExpExecArray | null
    let cursorX = x

    setBaseFont(baseStyle)
    doc.setFontSize(fontSize)

    while ((match = inlineRegex.exec(line)) !== null) {
      if (match.index > lastIndex) {
        const pre = line.slice(lastIndex, match.index)
        cursorX = renderTextWithSymbols(pre, baseStyle, fontSize, cursorX, yPos)
      }

      if (match[2]) {
        const boldText = match[2]
        cursorX = renderTextWithSymbols(boldText, "bold", fontSize, cursorX, yPos)
      } else if (match[4]) {
        const it = match[4]
        cursorX = renderTextWithSymbols(it, "italic", fontSize, cursorX, yPos)
      }

      lastIndex = match.index + match[0].length
    }

    if (lastIndex < line.length) {
      const rest = line.slice(lastIndex)
      cursorX = renderTextWithSymbols(rest, baseStyle, fontSize, cursorX, yPos)
    }
  }

  function renderParagraph(text: string, indent = 0) {
    const normalized = normalizeForPdf(text)
    const wrapped = doc.splitTextToSize(normalized, maxTextWidth - indent)

    for (const w of wrapped) {
      ensureSpace(lineGap + 2)
      renderInlineLine(w, marginX + indent, y)
      y += lineGap
    }
  }

  /**
   * Keep your intended visual: bullet + sign text inside item if present.
   * So "+ Capital" becomes "• + Capital" and "- Drawings" becomes "• - Drawings".
   */
  function renderList(items: string[]) {
    for (const raw of items) {
      const it = normalizeForPdf(raw.replace(/\*+/g, ""))
      if (!it) continue

      const wrapped = doc.splitTextToSize(it, maxTextWidth - 18)

      // bullet circle (safe glyph)
      ensureSpace(lineGap + 2)
      doc.setFillColor(0, 0, 0)
      doc.circle(marginX + 10, y - 4, 2, "F")

      for (const w of wrapped) {
        ensureSpace(lineGap + 2)
        renderInlineLine(w, marginX + 18, y)
        y += lineGap
      }
    }
    y += 4
  }

  function renderQuote(lines: string[]) {
    const startY = y
    ensureSpace((lines.length + 1) * lineGap + 6)

    doc.setDrawColor(200)
    doc.setLineWidth(3)
    doc.line(marginX + 5, y, marginX + 5, y + (lines.length + 1) * lineGap)
    doc.setLineWidth(0.2)
    doc.setDrawColor(0)

    for (const l of lines) {
      renderParagraph(l, 14)
    }

    y = Math.max(y, startY + (lines.length + 1) * lineGap)
    y += 6
  }

  function renderCodeBlock(lines: string[]) {
    const wrappedLines: string[] = []
    doc.setFont("courier", "normal")
    doc.setFontSize(codeFontSize)

    for (const l of lines) {
      const nl = normalizeForPdf(l || " ")
      const parts = doc.splitTextToSize(nl, maxTextWidth - 20)
      wrappedLines.push(...parts)
    }

    const padding = 6
    const blockHeight = wrappedLines.length * (codeFontSize + 4) + padding * 2
    ensureSpace(blockHeight + 6)

    doc.setFillColor(245, 245, 245)
    doc.rect(marginX + 10, y, maxTextWidth - 20, blockHeight, "F")
    doc.setDrawColor(210)
    doc.setLineWidth(0.5)
    doc.rect(marginX + 10, y, maxTextWidth - 20, blockHeight)

    let ty = y + padding + codeFontSize
    doc.setTextColor(20)
    for (const wl of wrappedLines) {
      doc.text(wl, marginX + 14, ty)
      ty += codeFontSize + 4
    }
    doc.setTextColor(0)

    setBaseFont("normal")
    doc.setFontSize(11)

    y += blockHeight + 10
  }

  function renderHeading(text: string, level = 2) {
    const normalized = normalizeForPdf(text)
    const size = Math.max(12, 20 - (level - 1) * 2)

    ensureSpace(size + 10)
    renderInlineLine(normalized, marginX + 6, y, { fontSize: size, baseStyle: "bold" })
    y += size + 6

    setBaseFont("normal")
    doc.setFontSize(11)
  }

  function renderTable(rows: string[][]) {
    if (!rows || rows.length === 0) return

    const colCount = Math.max(...rows.map((r) => r.length))
    const cellPadding = 6
    const startX = marginX + 10
    const tableWidth = maxTextWidth - 20

    // Compute column widths by measuring text (no bogus multipliers)
    const colWidths = new Array(colCount).fill(0).map(() => 40)
    setBaseFont("normal")
    doc.setFontSize(10)

    for (const r of rows) {
      for (let c = 0; c < colCount; c++) {
        const cell = normalizeForPdf(r[c] ?? "")
        colWidths[c] = Math.max(colWidths[c], doc.getTextWidth(cell) + cellPadding * 2)
      }
    }

    // Scale down to fit table width
    const total = colWidths.reduce((s, w) => s + w, 0) || colCount
    const scale = tableWidth / total
    for (let c = 0; c < colCount; c++) colWidths[c] = colWidths[c] * scale

    // Compute table height with wrapping
    const rowHeights: number[] = []
    for (let r = 0; r < rows.length; r++) {
      let rh = 18
      for (let c = 0; c < colCount; c++) {
        const cell = normalizeForPdf(rows[r][c] ?? "")
        const wrapped = doc.splitTextToSize(cell, colWidths[c] - cellPadding * 2)
        rh = Math.max(rh, wrapped.length * 12 + cellPadding * 2)
      }
      rowHeights.push(rh)
    }

    const totalHeight = rowHeights.reduce((s, h) => s + h, 0)
    ensureSpace(totalHeight + 14)

    doc.setDrawColor(200)
    doc.setLineWidth(0.5)

    let currentY = y
    for (let r = 0; r < rows.length; r++) {
      let x = startX
      const h = rowHeights[r]

      for (let c = 0; c < colCount; c++) {
        const cell = normalizeForPdf(rows[r][c] ?? "")
        doc.rect(x, currentY, colWidths[c], h)

        const wrapped = doc.splitTextToSize(cell, colWidths[c] - cellPadding * 2)

        let ty = currentY + cellPadding + 10
        for (const wl of wrapped) {
          renderInlineLine(wl, x + cellPadding, ty, { fontSize: 10, baseStyle: r === 0 ? "bold" : "normal" })
          ty += 12
        }

        x += colWidths[c]
      }

      currentY += h
    }

    setBaseFont("normal")
    doc.setFontSize(11)
    doc.setDrawColor(0)
    y = currentY + 16
  }

  // ----------------------------
  // Main render loop
  // ----------------------------
  for (const m of exportable) {
    const speaker = m.role === "user" ? "You" : m.role === "assistant" ? "AI Tutor" : "Error"

    ensureSpace(28)

    // speaker stripe + label
    doc.setFillColor(30, 120, 210)
    doc.rect(marginX, y - 12, 6, 18, "F")
    doc.setFillColor(0, 0, 0)

    setBaseFont("bold")
    doc.setFontSize(12)
    doc.text(`${speaker}:`, marginX + 12, y)
    y += 18

    setBaseFont("normal")
    doc.setFontSize(11)

    const segments = parseSegments(m.content ?? "")

    for (let si = 0; si < segments.length; si++) {
      const s = segments[si]

      switch (s.type) {
        case "para":
          renderParagraph(s.content)
          y += 10
          break

        case "heading": {
          // Keep heading with following table when possible
          const next = segments[si + 1]
          if (next && next.type === "table") {
            const minTableHeight = 18 * 2 + 20
            if (y + minTableHeight > pageHeight - bottomMargin) {
              doc.addPage()
              y = marginTop
            }
          }
          renderHeading(s.content, s.level)
          y += 2
          break
        }

        case "list":
          renderList(s.content)
          y += 6
          break

        case "quote":
          renderQuote(s.content)
          break

        case "code":
          renderCodeBlock(s.content)
          break

        case "table":
          renderTable(s.content)
          break
      }
    }

    y += 6
  }

  const safeCourse = (courseDisplayName ?? "conversation").replace(/[^\w\-]+/g, "_").slice(0, 40)
  doc.save(`ai_tutor_${safeCourse}_${now.toISOString().slice(0, 10)}.pdf`)
}
